/**
 * Stream A mock API — no MSW, no service worker.
 *
 * Monkey-patches window.fetch so every call to MOCK_API_ORIGIN or
 * MOCK_JWT_ISSUER is handled in-process from the static mock data
 * files. Real URLs pass through unchanged.
 *
 * Call `installMockApi()` once in main.ts before initFederation().
 * Angular's HttpClient must be provided with withFetch() so it goes
 * through the same interceptor.
 */

import { MOCK_API_ORIGIN, MOCK_JWT_ISSUER } from './config';
import { MOCK_USERS } from './data/users';
import { MOCK_ORGS, findOrg } from './data/orgs';
import { MOCK_APPROVALS } from './data/approvals';
import { MOCK_AUDIT_EVENTS } from './data/audit-events';
import { MOCK_BRANCHES, MOCK_DEPARTMENTS, MOCK_TEAMS } from './data/hierarchy';
import { MOCK_INVITATIONS } from './data/invitations';
import { MOCK_APP_MANIFESTS, MOCK_APP_CATALOG } from './data/apps';
import { RUNTIME_ORG_MEMBERS } from './data/org-members';

// ── Default persona loaded on first visit ──────────────────────────────────
// Derived from the fixtures rather than written out again. `whoami` already
// resolves an unrecognised email to MOCK_USERS[0], so a literal here agreed
// with that fallback only by coincidence -- and the same identity, copied into
// `mocks/config.ts`, had already drifted to a value matching no user at all.
// Deriving makes the default and the fallback one fact, so reordering or
// renaming the fixtures cannot leave a default pointing at nobody.
const DEFAULT_EMAIL = MOCK_USERS[0]!.email;
const DEFAULT_ORG_ID = 'acme';

// ── App-role → capability map ──────────────────────────────────────────────
// Locked 2026-05-08 per docs/gl/gl-devplan.md §9.
// Add entries when new roles or verbs are introduced; do not delete entries
// without bumping the doc.
const ROLE_CAPABILITIES: Readonly<Record<string, readonly string[]>> = {
  // ── Books — General Ledger ───────────────────────────────────────────────
  'books.admin': [
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.read',
    'app.klaro.books.gl.read',
    'app.klaro.books.gl.audit.read',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.write',
    // ── Reconcile with capability strings the GL components actually check.
    //    `books.admin` previously granted only 32 of the 40 GL caps the UI
    //    gates on, so a tenant admin (e.g. Brava Admin) was blocked from
    //    several GL CRUD actions. These close that gap (string mismatches
    //    like ic.match vs intercompany.match, plus view/run/coa/currency/
    //    recon/period/admin caps). A books admin gets full GL CRUD.
    'app.klaro.books.gl.read',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.audit.read',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.read',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.read',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.read',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.audit.read',
    'app.klaro.books.gl.read',
    'app.klaro.books.gl.admin',
    'app.klaro.books.gl.admin',
    'app.klaro.books.gl.admin',
  ],
  'books.accountant': [
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.write',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.read',
    'app.klaro.books.gl.read',
    'app.klaro.books.gl.write',
  ],
  'books.approver': [
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.manage',
    'app.klaro.books.gl.read',
    'app.klaro.books.gl.read',
  ],
  'books.auditor': [
    'app.klaro.books.gl.read',
    'app.klaro.books.gl.read',
    'app.klaro.books.gl.audit.read',
  ],
  'books.viewer': [
    'app.klaro.books.gl.read',
  ],

  // ── Books — Accounts Payable (Phase A11 — 2026-05-10) ───────────────────
  // Capability catalogue covers vendor / bill / payment / advance /
  // partial-payment / statement-recon / report / audit verbs. Role
  // separation per docs/ap/ap-userstories.md (AP_EXECUTIVE, AP_MANAGER,
  // FINANCE_MANAGER, CFO, COMPLIANCE_REVIEWER, CHARTERED_ACCOUNTANT).
  'books.ap.executive': [
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.write',
    // Bank-account maker verbs (AP-VM §7.2): add account, propose change, trigger re-verify.
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.report.read',
    'app.klaro.books.ap.write',
  ],
  'books.ap.manager': [
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.manage',
    // Bank-account maker + checker verbs (AP-VM §7.2 / §4.3 Maker-Checker).
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.report.read',
    'app.klaro.books.ap.report.read',
    'app.klaro.books.ap.write',
    'app.klaro.books.ap.audit.read',
  ],
  'books.ap.finance-manager': [
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.report.read',
    'app.klaro.books.ap.report.read',
    'app.klaro.books.ap.audit.read',
  ],
  'books.ap.cfo': [
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.manage',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.report.read',
    'app.klaro.books.ap.report.read',
    'app.klaro.books.ap.audit.read',
  ],
  'books.ap.compliance-reviewer': [
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.report.read',
    'app.klaro.books.ap.report.read',
    'app.klaro.books.ap.audit.read',
  ],
  'books.ap.chartered-accountant': [
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.read',
    'app.klaro.books.ap.report.read',
    'app.klaro.books.ap.report.read',
  ],
};

/** Resolve the union of capabilities across a user's app roles. */
function resolveCapabilities(appRoles: readonly string[]): readonly string[] {
  const set = new Set<string>();
  for (const role of appRoles) {
    const caps = ROLE_CAPABILITIES[role];
    if (caps) for (const c of caps) set.add(c);
  }
  return [...set];
}

// ── Helpers ────────────────────────────────────────────────────────────────

function ok(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'content-type': 'application/json' },
  });
}

function notFound(msg = 'Not found'): Response {
  return new Response(JSON.stringify({ error: msg }), {
    status: 404,
    headers: { 'content-type': 'application/json' },
  });
}

function extractBearer(req: Request): string | null {
  const auth = req.headers.get('authorization') ?? '';
  return auth.startsWith('Bearer ') ? auth.slice(7) : null;
}

/**
 * Token format: `email` (initial login) or `email|orgId` (after a workspace
 * switch). The activate endpoint mints the suffixed form so subsequent whoami
 * calls know which org is now active.
 */
function tokenToEmail(token: string | null): string {
  if (!token) return DEFAULT_EMAIL;
  const pipe = token.indexOf('|');
  return pipe === -1 ? token : token.slice(0, pipe);
}

function tokenToActiveOrgId(token: string | null): string | null {
  if (!token) return null;
  const pipe = token.indexOf('|');
  return pipe === -1 ? null : token.slice(pipe + 1);
}

/** PlanTier is a closed union; settings-page rows may use values outside it
 *  (e.g. "business"). Coerce anything unknown to the safe default. */
type StubPlan = 'free' | 'starter' | 'growth' | 'enterprise';
function coercePlan(plan: string): StubPlan {
  switch (plan) {
    case 'free':
    case 'starter':
    case 'growth':
    case 'enterprise':
      return plan;
    default:
      return 'growth';
  }
}

/** Shared shape for the two helpers below — must be assignable to the
 *  same row type the `orgs.map(...)` call in buildCurrentUser produces.
 *  Explicit so `plan` widens to the full StubPlan union (otherwise the
 *  inferred type narrows to literal `'growth'` via the `as const` on the
 *  synthesizer, and the stashed path with its variable plan won't fit). */
interface MockOrgRow {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly plan: StubPlan;
  readonly status: 'ACTIVE';
  readonly role: 'org_owner';
  readonly countryCode: string;
  readonly tier: string;
  readonly memberCount: number;
  readonly initials: string;
}

/**
 * Read the per-org display stash written by the settings-page workflows
 * before invoking switchOrg. Returns the unmarshalled entry shaped like a
 * MOCK_ORGS row, or null if nothing was stashed for this id.
 */
function readStashedOrg(orgId: string): MockOrgRow | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem('klaro-mock-org-stash:' + orgId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      id: string;
      name: string;
      initials: string;
      plan: string;
      tier: string;
      memberCount: number;
    };
    return {
      id: parsed.id,
      name: parsed.name,
      slug: parsed.id,
      plan: coercePlan(parsed.plan),
      status: 'ACTIVE',
      role: 'org_owner',
      countryCode: 'US',
      tier: parsed.tier,
      memberCount: parsed.memberCount,
      initials: parsed.initials,
    };
  } catch {
    return null;
  }
}

/**
 * Build a minimal-but-valid org entry from a slug-style id, used when no
 * caller-supplied stash exists. "klaro-tech" → "Klaro Tech", capitalised
 * per word; defaults to Growth plan + 25 members so the brand pill renders
 * without missing-data warnings.
 */
function synthesizeOrgFromId(orgId: string, countryCode: string): MockOrgRow {
  const name = orgId
    .split('-')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
  return {
    id: orgId,
    name,
    slug: orgId,
    plan: 'growth',
    status: 'ACTIVE',
    role: 'org_owner',
    countryCode,
    tier: 'Growth',
    memberCount: 25,
    initials: name.slice(0, 2).toUpperCase(),
  };
}

function buildCurrentUser(email: string, activeOrgId: string) {
  const user = MOCK_USERS.find((u) => u.email === email) ?? MOCK_USERS[0]!;

  // Demo persona is a platform super_admin — surface every mock org in the
  // brand-switcher so the dropdown is populated. Per-org `role` stays as
  // declared in MOCK_ORGS (Owner on Acme, Admin on Acme EU, etc.) so the
  // role chip still matches when the user switches workspace.
  const orgs = MOCK_ORGS.map((o) => ({
    id: o.id,
    name: o.name,
    slug: o.slug,
    plan: o.plan,
    status: o.status,
    role: o.role,
    countryCode: o.countryCode,
    tier: o.tier,
    memberCount: o.memberCount,
    initials: o.initials,
  }));

  // If the activate request targeted an org NOT in our canonical MOCK_ORGS
  // (typical when the user clicks Manage on the admin orgs page, whose IDs
  // belong to a separate mock dataset), inject a synthetic entry so
  // OrgService.currentOrg resolves to *that* org rather than falling back
  // to orgs[0]. The brand pill then reflects the switch.
  //
  // We prefer the row data the caller stashed (real name, plan, initials)
  // over a slug-derived fallback so the brand pill matches what the user
  // clicked. Phase 7 backend returns the canonical row from /v1/whoami and
  // this synthesis path goes away.
  if (!orgs.some((o) => o.id === activeOrgId)) {
    const stashed = readStashedOrg(activeOrgId);
    if (stashed) {
      orgs.unshift(stashed);
    } else {
      orgs.unshift(synthesizeOrgFromId(activeOrgId, user.countryCode));
    }
  }

  return {
    sub: user.id,
    email: user.email,
    displayName: user.displayName,
    tenantId: activeOrgId,
    countryCode: user.countryCode,
    platformRoles: ['super_admin'],
    appRoles: user.appRoles,
    // Demo persona is a platform super_admin — grant the canonical
    // admin capability set so every admin surface (roles, members,
    // billing, branding, security, hierarchy, audit) renders its
    // manage affordances rather than the "Requires X" stub.
    capabilities: [
      'settings.read',
      'settings.write',
      'users.read',
      'users.invite',
      'users.manage',
      'org.read',
      'org.manage',
      'org.roles.read',
      'org.roles.manage',
      'org.billing.read',
      'org.billing.manage',
      'org.branding.manage',
      'org.security.manage',
      'org.hierarchy.manage',
      'org.audit.read',
      'org.apps.manage',
      // App-role capabilities (books.*, etc.) resolved from the user's
      // appRoles. Without this the whoami carried ONLY platform/org caps,
      // so every books GL/AP CRUD affordance (gated on books.* caps) was
      // hidden for all users — the platformRoles super_admin bypass can't
      // help because the __klaroShellUser bridge doesn't carry platformRoles.
      ...resolveCapabilities(user.appRoles),
    ],
    correlationId: `mock-corr-${user.id}`,
    orgs,
    activeOrgId,
  };
}

// ── URL parser ─────────────────────────────────────────────────────────────

function parseUrl(raw: string) {
  const url = new URL(raw);
  return { pathname: url.pathname, search: url.searchParams };
}

// ── Auth issuer handlers ────────────────────────────────────────────────────

async function handleIssuer(req: Request, pathname: string): Promise<Response | null> {
  if (pathname === '/oauth/token' && req.method === 'POST') {
    const body = await req.text();
    const params = new URLSearchParams(body);
    const username = params.get('username') ?? DEFAULT_EMAIL;
    // Token = email (decoded by whoami)
    return ok({ access_token: username, token_type: 'Bearer', expires_in: 86400 });
  }
  return null;
}

// ── API handlers ────────────────────────────────────────────────────────────

function handleApi(req: Request, pathname: string, search: URLSearchParams): Response | null {
  const token = extractBearer(req);
  const email = tokenToEmail(token);

  // GET /v1/whoami
  if (pathname === '/v1/whoami' && req.method === 'GET') {
    const user = MOCK_USERS.find((u) => u.email === email) ?? MOCK_USERS[0]!;
    // Active-org resolution: installer pre-processes the bearer and
    // moves the |orgId suffix into x-mock-active-org. Read that
    // header FIRST; fall back to in-token suffix (for callers that
    // bypass the installer) and finally the user's default tenant.
    const headerOrg = req.headers.get('x-mock-active-org');
    const activeOrgId = headerOrg || tokenToActiveOrgId(token) || user.tenantId;
    return ok(buildCurrentUser(email, activeOrgId));
  }

  // GET /v1/apps  (installed list + catalog view)
  if (pathname === '/v1/apps' && req.method === 'GET') {
    if (search.get('view') === 'catalog') {
      return ok({ items: MOCK_APP_CATALOG, totalCount: MOCK_APP_CATALOG.length });
    }
    return ok({ items: MOCK_APP_MANIFESTS, totalCount: MOCK_APP_MANIFESTS.length });
  }

  // GET /v1/users
  if (pathname === '/v1/users' && req.method === 'GET') {
    const user = MOCK_USERS.find((u) => u.email === email) ?? MOCK_USERS[0]!;
    const orgId = user.tenantId;
    let items = MOCK_USERS.filter((u) => u.tenantId === orgId);
    const q = search.get('q')?.toLowerCase();
    if (q) items = items.filter((u) => u.displayName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
    const status = search.get('status');
    if (status) items = items.filter((u) => u.status === status);
    const limit = parseInt(search.get('limit') ?? '20', 10);
    return ok({
      items,
      pagination: { totalCount: items.length, nextCursor: null, limit },
    });
  }

  // GET /v1/users/invitations
  if (pathname === '/v1/users/invitations' && req.method === 'GET') {
    const user = MOCK_USERS.find((u) => u.email === email) ?? MOCK_USERS[0]!;
    const items = MOCK_INVITATIONS.filter((i) => i.tenantId === user.tenantId);
    return ok({ items, totalCount: items.length });
  }

  // POST /v1/users/invitations
  if (pathname === '/v1/users/invitations' && req.method === 'POST') {
    return ok({
      id: `mock-inv-${Date.now()}`,
      tenantId: 'acme',
      email: 'new@example.com',
      role: 'org_member',
      invitedByUserId: 'mock',
      status: 'pending',
      sentAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 7 * 864e5).toISOString(),
      correlationId: `mock-corr-${Date.now()}`,
    }, 201);
  }

  // GET /v1/users/:id
  const userMatch = pathname.match(/^\/v1\/users\/([^/]+)$/);
  if (userMatch && req.method === 'GET') {
    const found = MOCK_USERS.find((u) => u.id === userMatch[1]);
    return found ? ok(found) : notFound('User not found');
  }

  // DELETE /v1/users/invitations/:id
  const invDelMatch = pathname.match(/^\/v1\/users\/invitations\/([^/]+)$/);
  if (invDelMatch && req.method === 'DELETE') {
    return new Response(null, { status: 204 });
  }

  // GET /v1/approvals
  if (pathname === '/v1/approvals' && req.method === 'GET') {
    const user = MOCK_USERS.find((u) => u.email === email) ?? MOCK_USERS[0]!;
    let items = [...MOCK_APPROVALS.filter((a) => a.orgId === user.tenantId)];
    const status = search.get('status');
    if (status) items = items.filter((a) => a.status === status);
    return ok({ items, totalCount: items.length });
  }

  // POST /v1/approvals/:id/approve
  const approveMatch = pathname.match(/^\/v1\/approvals\/([^/]+)\/approve$/);
  if (approveMatch && req.method === 'POST') {
    const approval = MOCK_APPROVALS.find((a) => a.id === approveMatch[1]);
    if (!approval) return notFound('Approval not found');
    approval.status = 'approved';
    approval.decisionByUserId = email;
    approval.decisionAt = new Date().toISOString();
    return ok(approval);
  }

  // POST /v1/approvals/:id/reject
  const rejectMatch = pathname.match(/^\/v1\/approvals\/([^/]+)\/reject$/);
  if (rejectMatch && req.method === 'POST') {
    const approval = MOCK_APPROVALS.find((a) => a.id === rejectMatch[1]);
    if (!approval) return notFound('Approval not found');
    approval.status = 'rejected';
    approval.decisionByUserId = email;
    approval.decisionAt = new Date().toISOString();
    return ok(approval);
  }

  // GET /v1/audit-events
  if (pathname === '/v1/audit-events' && req.method === 'GET') {
    const user = MOCK_USERS.find((u) => u.email === email) ?? MOCK_USERS[0]!;
    let items = [...MOCK_AUDIT_EVENTS.filter((e) => e.orgId === user.tenantId)];
    const actor = search.get('actor')?.toLowerCase();
    if (actor) items = items.filter((e) => e.actorName.toLowerCase().includes(actor));
    const severity = search.get('severity');
    if (severity) items = items.filter((e) => e.severity === severity);
    const type = search.get('type');
    if (type) items = items.filter((e) => e.type.startsWith(type));
    return ok({ items, totalCount: items.length });
  }

  // GET /v1/hierarchy/branches
  if (pathname === '/v1/hierarchy/branches' && req.method === 'GET') {
    const user = MOCK_USERS.find((u) => u.email === email) ?? MOCK_USERS[0]!;
    const items = MOCK_BRANCHES.filter((b) => b.orgId === user.tenantId).map((b) => ({
      ...b,
      branchName: b.name,
    }));
    return ok({ items, totalCount: items.length });
  }

  // GET /v1/hierarchy/departments
  if (pathname === '/v1/hierarchy/departments' && req.method === 'GET') {
    const user = MOCK_USERS.find((u) => u.email === email) ?? MOCK_USERS[0]!;
    const branchId = search.get('branchId');
    let items = MOCK_DEPARTMENTS.filter((d) => d.orgId === user.tenantId);
    if (branchId) items = items.filter((d) => d.branchId === branchId);
    const withNames = items.map((d) => ({
      ...d,
      branchName: MOCK_BRANCHES.find((b) => b.id === d.branchId)?.name ?? '',
    }));
    return ok({ items: withNames, totalCount: withNames.length });
  }

  // GET /v1/hierarchy/teams
  if (pathname === '/v1/hierarchy/teams' && req.method === 'GET') {
    const user = MOCK_USERS.find((u) => u.email === email) ?? MOCK_USERS[0]!;
    const branchId = search.get('branchId');
    const departmentId = search.get('departmentId');
    let items = MOCK_TEAMS.filter((t) => t.orgId === user.tenantId);
    if (branchId) items = items.filter((t) => t.branchId === branchId);
    if (departmentId) items = items.filter((t) => t.departmentId === departmentId);
    const withNames = items.map((t) => ({
      ...t,
      departmentName: MOCK_DEPARTMENTS.find((d) => d.id === t.departmentId)?.name ?? '',
    }));
    return ok({ items: withNames, totalCount: withNames.length });
  }

  // GET /v1/orgs/:id
  const orgMatch = pathname.match(/^\/v1\/orgs\/([^/]+)$/);
  if (orgMatch && req.method === 'GET') {
    const org = findOrg(orgMatch[1]!);
    return org ? ok(org) : notFound('Org not found');
  }

  // POST /v1/orgs/:id/activate
  const activateMatch = pathname.match(/^\/v1\/orgs\/([^/]+)\/activate$/);
  if (activateMatch && req.method === 'POST') {
    const orgId = activateMatch[1]!;
    return ok({
      access_token: `${email}|${orgId}`,
      token_type: 'Bearer',
      expires_in: 86400,
      active_org_id: orgId,
    });
  }

  // ── Org-member shell endpoints  /api/v1/shell/org-members/** ──────────────
  // These mirror the auth-service ShellOrgMemberController and return the
  // ApiResponse<T> envelope shape: { success, data, message }.

  /** Resolve the active org id for org-member routes.
   *  The installer pre-processes org-switch tokens — it strips the
   *  "email|orgId" form and moves orgId into x-mock-active-org. We
   *  read that header first, then fall back to the raw token pipe, and
   *  finally to the user's default tenant. */
  const callerOrgId = (): string => {
    const headerOrg = req.headers.get('x-mock-active-org');
    if (headerOrg) return headerOrg;
    const pipeIdx = token ? token.indexOf('|') : -1;
    if (pipeIdx !== -1) return token!.slice(pipeIdx + 1);
    const user = MOCK_USERS.find((u) => u.email === email);
    return user?.tenantId ?? 'acme';
  };

  const apiOk = <T>(data: T, message: string | null = null, status = 200): Response =>
    new Response(JSON.stringify({ success: true, data, message }), {
      status,
      headers: { 'content-type': 'application/json' },
    });

  // GET /api/v1/shell/org-members        — all members for caller's org
  // GET /api/v1/shell/org-members/active — active only
  // GET /api/v1/shell/org-members/pending — invited only
  if (
    (pathname === '/api/v1/shell/org-members' ||
     pathname === '/api/v1/shell/org-members/active' ||
     pathname === '/api/v1/shell/org-members/pending') &&
    req.method === 'GET'
  ) {
    const orgId = callerOrgId();
    let rows = RUNTIME_ORG_MEMBERS.filter((m) => m.orgId === orgId);
    if (pathname.endsWith('/active'))  rows = rows.filter((m) => m.status === 'ACTIVE');
    if (pathname.endsWith('/pending')) rows = rows.filter((m) => m.status === 'INVITED');
    return apiOk(rows);
  }

  // POST /api/v1/shell/org-members/invite
  if (pathname === '/api/v1/shell/org-members/invite' && req.method === 'POST') {
    return apiOk(null, 'org.member.invite.sent', 201);
  }

  // DELETE /api/v1/shell/org-members/:userId
  const orgMemberDeleteMatch = pathname.match(/^\/api\/v1\/shell\/org-members\/([^/]+)$/);
  if (orgMemberDeleteMatch && req.method === 'DELETE') {
    const targetUserId = orgMemberDeleteMatch[1]!;
    const idx = RUNTIME_ORG_MEMBERS.findIndex(
      (m) => m.id === targetUserId || m.userId === targetUserId,
    );
    if (idx !== -1) RUNTIME_ORG_MEMBERS.splice(idx, 1);
    return apiOk(null, 'org.member.removed');
  }

  // ── Public invitation preview  /api/v1/org-invitations/{token}/preview ─────
  // This endpoint is called unauthenticated — no auth header required.
  // It returns enough org/inviter context to render the landing page.
  const invitePreviewMatch = pathname.match(/^\/api\/v1\/org-invitations\/([^/]+)\/preview$/);
  if (invitePreviewMatch && req.method === 'GET') {
    const inviteToken = invitePreviewMatch[1]!;
    if (!inviteToken || inviteToken === 'expired-token') {
      return new Response(
        JSON.stringify({ success: false, message: 'org.invite.expired' }),
        { status: 410, headers: { 'content-type': 'application/json' } },
      );
    }
    // Return a generic preview. In production the token is resolved
    // against the klr_org_members table in org-service.
    return apiOk({
      orgName:     'Acme Corp',
      inviterName: 'Acme Owner',
      role:        'Member',
      expiresAt:   new Date(Date.now() + 7 * 864e5).toISOString(),
    });
  }

  // POST /api/v1/org-invitations/{token}/decline
  const inviteDeclineMatch = pathname.match(/^\/api\/v1\/org-invitations\/([^/]+)\/decline$/);
  if (inviteDeclineMatch && req.method === 'POST') {
    return apiOk(null, 'org.invite.declined');
  }

  return null;
}

// ── Synchronous user bootstrap ─────────────────────────────────────────────

/**
 * Build a CurrentUser object synchronously from a stored email.
 * Used by AuthService to pre-populate `currentUser` on the first render cycle
 * so the dashboard never shows "Welcome, " with an empty name.
 * Returns null if the email is not recognised in MOCK_USERS.
 */
export function buildMockUser(email: string): ReturnType<typeof buildCurrentUser> | null {
  const user = MOCK_USERS.find((u) => u.email === email);
  if (!user) return null;
  return buildCurrentUser(email, user.tenantId);
}

// ── Installer ──────────────────────────────────────────────────────────────

export function installMockApi(): void {
  const realFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const req = new Request(input, init);
    const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : req.url;

    if (raw.startsWith(MOCK_JWT_ISSUER)) {
      const { pathname } = parseUrl(raw);
      const result = await handleIssuer(req, pathname);
      if (result) return result;
    }

    if (raw.startsWith(MOCK_API_ORIGIN)) {
      const { pathname, search } = parseUrl(raw);
      // whoami may carry an org-switch token of form "email|orgId"
      const authHeader = req.headers.get('authorization') ?? '';
      const rawToken = authHeader.startsWith('Bearer ') ? authHeader.slice(7) : '';
      const [emailPart, orgPart] = rawToken.split('|');
      const patchedReq = orgPart
        ? new Request(raw, {
            method: req.method,
            headers: (() => {
              const h = new Headers(req.headers);
              h.set('authorization', `Bearer ${emailPart}`);
              h.set('x-mock-active-org', orgPart);
              return h;
            })(),
            body: req.body,
            duplex: 'half',
          } as RequestInit)
        : req;

      const result = handleApi(patchedReq, pathname, search);
      if (result) return result;
    }

    return realFetch(input, init);
  };
}

// ── Auto-persist default persona on first visit ────────────────────────────

export function ensureDefaultPersona(): void {
  const KEY = 'klaro-mock-persona';
  const SIGNED_OUT_KEY = 'klaro-mock-signed-out';
  if (!localStorage.getItem(KEY) && !localStorage.getItem(SIGNED_OUT_KEY)) {
    localStorage.setItem(KEY, DEFAULT_EMAIL);
  }
}
